'use client';

import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { createClient } from '../lib/supabase/client';

export type SectionKey =
  | 'inventories' | 'signals' | 'opportunities' | 'reachout' | 'mediaPlanner' | 'aiSalesAgent' | 'rfpEngine';

interface SectionTime {
  inventories: number;
  signals: number;
  opportunities: number;
  reachout: number;
  mediaPlanner: number;
  aiSalesAgent: number;
  rfpEngine: number;
}

interface SessionContextValue {
  userEmail: string | null;
  setUserEmail: (email: string) => void;
  clearSession: () => void;
  isAuthenticated: boolean;
  initialized: boolean;
  activeSection: SectionKey | null;
  setActiveSection: (section: SectionKey | null) => void;
  sectionTimes: SectionTime;
}

const SessionContext = createContext<SessionContextValue>({
  userEmail: null,
  setUserEmail: () => {},
  clearSession: () => {},
  isAuthenticated: false,
  initialized: false,
  activeSection: null,
  setActiveSection: () => {},
  sectionTimes: {
    inventories: 0,
    signals: 0,
    opportunities: 0,
    reachout: 0,
    mediaPlanner: 0,
    aiSalesAgent: 0,
    rfpEngine: 0,
  },
});

export const useSession = () => useContext(SessionContext);

/** Flush accumulated time for the currently active section into the times snapshot */
function flushActiveSection(
  times: SectionTime,
  activeSection: SectionKey | null,
  sectionStart: number | null
): SectionTime {
  if (!activeSection || !sectionStart) return times;
  const elapsed = Math.floor((Date.now() - sectionStart) / 1000);
  return { ...times, [activeSection]: (times[activeSection] || 0) + elapsed };
}

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [userEmail, setUserEmailState] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessionStart, setSessionStart] = useState<string | null>(null);
  const [activeSection, setActiveSectionState] = useState<SectionKey | null>(null);
  const [sectionTimes, setSectionTimes] = useState<SectionTime>({
    inventories: 0,
    signals: 0,
    opportunities: 0,
    reachout: 0,
    mediaPlanner: 0,
    aiSalesAgent: 0,
    rfpEngine: 0,
  });

  const activeSectionRef = useRef<SectionKey | null>(null);
  const sectionStartRef = useRef<number | null>(null);
  const sectionTimesRef = useRef<SectionTime>(sectionTimes);
  const userEmailRef = useRef<string | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const sessionStartRef = useRef<string | null>(null);

  // Keep refs in sync with state
  useEffect(() => { sectionTimesRef.current = sectionTimes; }, [sectionTimes]);

  // Restore session from localStorage on mount
  useEffect(() => {
    try {
      const storedEmail = localStorage.getItem('doohthis_user_email');
      if (storedEmail) {
        setUserEmailState(storedEmail);
        userEmailRef.current = storedEmail;
      }
    } catch {}
    setInitialized(true);
  }, []);

  /** Save current session times to Supabase (called periodically and on section change) */
  const persistToSupabase = useCallback(async (times: SectionTime) => {
    const sid = sessionIdRef.current;
    if (!sid) return;
    try {
      const supabase = createClient();
      await supabase
        .from('user_sessions')
        .update({
          time_inventories: times.inventories,
          time_signals: times.signals,
          time_opportunities: times.opportunities,
          time_reachout: times.reachout,
          time_media_planner: times.mediaPlanner,
          time_ai_sales_agent: times.aiSalesAgent,
          time_rfp_engine: times.rfpEngine,
        })
        .eq('id', sid);
    } catch {}
  }, []);

  // Continuously save to Supabase every 30 seconds so data is never lost
  useEffect(() => {
    const interval = setInterval(() => {
      if (!userEmailRef.current || !sessionIdRef.current) return;
      // Include any in-progress section time before saving
      const flushed = flushActiveSection(
        sectionTimesRef.current,
        activeSectionRef.current,
        sectionStartRef.current
      );
      persistToSupabase(flushed);
    }, 30_000);
    return () => clearInterval(interval);
  }, [persistToSupabase]);

  const setUserEmail = useCallback(async (email: string) => {
    const supabase = createClient();
    const start = new Date().toISOString();

    try {
      const { data, error } = await supabase
        .from('user_sessions')
        .insert({ email, session_start: start })
        .select('id')
        .single();

      if (!error && data) {
        const id = data.id;
        setSessionId(id);
        sessionIdRef.current = id;
      }
    } catch {}

    setUserEmailState(email);
    userEmailRef.current = email;
    setSessionStart(start);
    sessionStartRef.current = start;

    // Persist email to localStorage so session survives page refresh
    try {
      localStorage.setItem('doohthis_user_email', email);
    } catch {}
  }, []);

  const clearSession = useCallback(() => {
    // Flush active section time before sending the final beacon
    const email = userEmailRef.current;
    const sid = sessionIdRef.current;

    if (email && sid) {
      const times = flushActiveSection(
        sectionTimesRef.current,
        activeSectionRef.current,
        sectionStartRef.current
      );
      const sessionEnd = new Date().toISOString();
      const payload = JSON.stringify({ sessionId: sid, email, sessionEnd, times });
      navigator.sendBeacon('/api/session-end', new Blob([payload], { type: 'application/json' }));
    }

    setUserEmailState(null);
    userEmailRef.current = null;
    setSessionId(null);
    sessionIdRef.current = null;
    setSessionStart(null);
    sessionStartRef.current = null;
    setActiveSectionState(null);
    activeSectionRef.current = null;
    sectionStartRef.current = null;
    const emptyTimes = { inventories: 0, signals: 0, opportunities: 0, reachout: 0, mediaPlanner: 0, aiSalesAgent: 0, rfpEngine: 0 };
    setSectionTimes(emptyTimes);
    sectionTimesRef.current = emptyTimes;

    // Clear persisted email
    try {
      localStorage.removeItem('doohthis_user_email');
    } catch {}
  }, []);

  const setActiveSection = useCallback((section: SectionKey | null) => {
    const now = Date.now();

    // Accumulate time for the section that was active
    if (activeSectionRef.current && sectionStartRef.current) {
      const elapsed = Math.floor((now - sectionStartRef.current) / 1000);
      setSectionTimes((prev) => {
        const updated = {
          ...prev,
          [activeSectionRef.current!]: prev[activeSectionRef.current!] + elapsed,
        };
        sectionTimesRef.current = updated;
        // Persist to Supabase on every section change
        persistToSupabase(updated);
        return updated;
      });
    }

    activeSectionRef.current = section;
    setActiveSectionState(section);
    sectionStartRef.current = section ? now : null;
  }, [persistToSupabase]);

  // On tab close: use sendBeacon to same-origin API route (no auth header needed, reliable)
  useEffect(() => {
    const handleBeforeUnload = () => {
      const email = userEmailRef.current;
      const sid = sessionIdRef.current;
      if (!email || !sid) return;

      // Flush current active section time
      const times = flushActiveSection(
        sectionTimesRef.current,
        activeSectionRef.current,
        sectionStartRef.current
      );

      const sessionEnd = new Date().toISOString();

      const payload = JSON.stringify({
        sessionId: sid,
        email,
        sessionEnd,
        times,
      });

      // sendBeacon to same-origin Next.js API route — no custom headers needed,
      // browser guarantees delivery even after tab close
      navigator.sendBeacon('/api/session-end', new Blob([payload], { type: 'application/json' }));
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  return (
    <SessionContext.Provider
      value={{
        userEmail,
        setUserEmail,
        clearSession,
        isAuthenticated: !!userEmail,
        initialized,
        activeSection,
        setActiveSection,
        sectionTimes,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
}
